import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Mail01Icon,
  Message01Icon,
  SentIcon,
  TextIcon,
} from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'
import { useSendEmail } from '../email.hooks'
import type { OpenEmailOptions } from '../email.types'
import { type RecipientOption } from '@/modules/whatsapp/components/recipient-combobox'
import { MessageRecipientField } from '@/modules/whatsapp/components/message-recipient-field'
import { TemplatePicker } from '@/modules/templates/components/template-picker'
import {
  fillTemplate,
  useBranchName,
} from '@/modules/templates/template-variables'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

const isEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export function EmailComposerSheet({
  open,
  onOpenChange,
  options,
  session,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: OpenEmailOptions | null
  session: number
}) {
  const send = useSendEmail()
  const branchName = useBranchName()

  const [recipient, setRecipient] = useState<RecipientOption | null>(null)
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  // A recipient handed in from a profile is fixed (no "Cambiar").
  const [locked, setLocked] = useState(false)

  useEffect(() => {
    if (options?.recipient) {
      setRecipient({
        id: options.recipient.id ?? '',
        type: options.recipient.type,
        name: options.recipient.name,
        firstName: options.recipient.firstName,
        phone: '',
        email: options.recipient.email,
      })
      setEmail(options.recipient.email ?? '')
      setLocked(true)
    } else {
      setRecipient(null)
      setEmail('')
      setLocked(false)
    }
    setSubject(options?.subject ?? '')
    setBody(options?.body ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  const clearRecipient = () => {
    setRecipient(null)
    setEmail('')
  }

  const canSend =
    !!recipient &&
    isEmail(email) &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    !send.isPending

  const handleSend = () => {
    if (!canSend || !recipient) return
    send.mutate(
      {
        to: email.trim(),
        subject: subject.trim(),
        body: body.trim(),
        recipientType: recipient.type,
        recipientId: recipient.id || undefined,
        recipientName: recipient.name,
      },
      {
        onSuccess: () => {
          toast.success('Correo enviado')
          onOpenChange(false)
        },
        onError: (error: any) =>
          toast.error(error?.message || 'No se pudo enviar el correo'),
      },
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full gap-0 data-[side=right]:sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Enviar correo</SheetTitle>
          <SheetDescription>
            Escribe un correo directo a un contacto, estudiante o profesor.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 overflow-y-auto px-6 py-4">
          <MessageRecipientField
            recipient={recipient}
            locked={locked}
            onClear={clearRecipient}
            onSelect={(next) => {
              setRecipient(next)
              setEmail(next.email ?? '')
            }}
          >
            {recipient?.email && isEmail(email) ? (
              <span className="truncate text-xs text-muted-foreground">
                {email}
              </span>
            ) : (
              <Input
                type="email"
                inputMode="email"
                placeholder="correo@ejemplo.com"
                aria-label="Correo del destinatario"
                className="mt-1 h-8"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </MessageRecipientField>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="email-subject"
              className="flex items-center gap-1.5"
            >
              <HugeiconsIcon
                icon={TextIcon}
                className="size-4 text-muted-foreground"
              />
              Asunto
            </Label>
            <Input
              id="email-subject"
              placeholder="Asunto del correo"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              maxLength={200}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="email-body" className="flex items-center gap-1.5">
                <HugeiconsIcon
                  icon={Message01Icon}
                  className="size-4 text-muted-foreground"
                />
                Mensaje
              </Label>
              <TemplatePicker
                channel="email"
                onSelect={(template) => {
                  const values = {
                    nombre: recipient?.firstName ?? recipient?.name,
                    nombre_completo: recipient?.name,
                    sucursal: branchName,
                  }
                  if (template.subject)
                    setSubject(fillTemplate(template.subject, values))
                  setBody(fillTemplate(template.body, values))
                }}
              />
            </div>
            <Textarea
              id="email-body"
              placeholder="Escribe el mensaje…"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={10000}
              className={cn('min-h-44 resize-none')}
            />
            <span className="self-end text-xs tabular-nums text-muted-foreground">
              {body.length} / 10000
            </span>
          </div>
        </div>

        <SheetFooter className="mt-0 flex-row justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend}>
            <HugeiconsIcon
              icon={send.isPending ? Mail01Icon : SentIcon}
              data-icon="inline-start"
            />
            {send.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
